/**
 * Portal two-factor (TOTP) policy (AIR-1500) — the pure half.
 *
 * Identity Platform enforces nothing: a user with no enrolled factor signs
 * in with a password alone, and next-firebase-auth-edge re-mints the session
 * from a custom token, so the session's decoded token never carries
 * Firebase's `firebase.sign_in_second_factor` claim. The server therefore
 * cannot tell from `__session` whether a second factor was passed. Instead,
 * right after sign-in (or enrolment) the browser proves it with a real
 * Firebase ID token at POST /api/mfa/attest, which answers with the `__mfa`
 * attestation cookie minted here: an HMAC over { uid, sat, iat } where `sat`
 * is the SESSION's auth_time. A new login mints a new session with a new
 * auth_time, so the attestation dies with the session it vouched for.
 *
 * No Next.js or Node-only imports: Web Crypto so the verifier would also run
 * in the edge runtime, and so the policy is unit-testable in isolation.
 */

export const MFA_COOKIE_NAME = "__mfa";

/** The session cookie's own lifetime; binding to auth_time is the real guard. */
export const MFA_COOKIE_MAX_AGE_SECONDS = 14 * 24 * 60 * 60;

/**
 * How recent the ID token's auth_time must be for an enrolled-but-not-
 * challenged token to attest (the fresh-enrolment path: Firebase requires a
 * sign-in or re-authentication within the last few minutes to enrol).
 */
export const MFA_FRESH_ENROLMENT_WINDOW_SECONDS = 10 * 60;

export const MFA_PAGE_PATH = "/mfa";

/**
 * Request header the auth proxy stamps with the request's own path + query
 * (proxy.ts, overwriting anything the client sent) so the (main) layout —
 * a server component with no other view of the URL — knows which page it is
 * gating and where to send the user back to after the challenge.
 */
export const REQUEST_PATH_HEADER = "x-aireserve-path";

/** Plain-language refusal used by server actions and API routes. */
export const MFA_REQUIRED_MESSAGE = "Finish two-factor sign-in to continue.";

export type MfaRequirementReason = "organization";

export interface MfaPolicyInput {
  /**
   * Custom-claim role: member | enterprise_admin | super_admin. Carried for
   * the audit trail and future rules; it does not change the answer today.
   * Super admins are deliberately NOT required (Ashwin, 2026-10-07: demo
   * accounts stay frictionless; they may enrol voluntarily on Profile).
   */
  role?: string | null;
  /** The org's `client.settings.mfaRequired`. */
  orgMfaRequired?: boolean | null;
  /** The ORIGINAL sign-in provider (`source_sign_in_provider` on a session token). */
  signInProvider?: string | null;
}

/** SSO sign-ins satisfy the requirement: the customer's IdP enforces MFA. */
export function isSsoSignInProvider(signInProvider: string | null | undefined): boolean {
  return typeof signInProvider === "string" && signInProvider.startsWith("saml.");
}

/**
 * Why two-factor is required for this principal, or null when it is not.
 * Off by default for everyone: the only rule is the organization's own
 * opt-in, and an SSO sign-in satisfies it because the customer's IdP
 * enforces MFA.
 */
export function mfaRequirementReasonFor({ orgMfaRequired, signInProvider }: MfaPolicyInput): MfaRequirementReason | null {
  if (isSsoSignInProvider(signInProvider)) return null;
  if (orgMfaRequired === true) return "organization";
  return null;
}

export function mfaRequiredFor(input: MfaPolicyInput): boolean {
  return mfaRequirementReasonFor(input) !== null;
}

/**
 * Paths a session may use before it is attested: the challenge/enrolment
 * surface itself, the sign-in round trip, and the public pages. Mirrors the
 * public-path list (lib/auth/public-paths.ts) without importing it so this
 * module stays dependency-free; the gate ORs the two.
 */
const MFA_EXEMPT_PATHS = [
  MFA_PAGE_PATH,
  "/login",
  "/logout",
  "/api/mfa",
  "/api/login",
  "/api/logout",
  "/unauthorized",
  "/set-password",
];

export function isMfaExemptPath(pathname: string): boolean {
  return MFA_EXEMPT_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/** `/mfa?redirect=<path>` for a path worth returning to, bare `/mfa` otherwise. */
export function mfaChallengePath(returnTo: string | null | undefined): string {
  if (!returnTo || returnTo === "/" || !isSameOriginPath(returnTo)) {
    return MFA_PAGE_PATH;
  }
  return `${MFA_PAGE_PATH}?redirect=${encodeURIComponent(returnTo)}`;
}

/**
 * A relative path that cannot leave the origin: one leading slash, no
 * protocol-relative `//`, and no backslash anywhere — browsers normalise
 * `/\evil.com` to `//evil.com` (the case lib/auth/safe-redirect.ts documents),
 * so a backslash is rejected outright rather than normalised.
 */
export function isSameOriginPath(value: string): boolean {
  return value.startsWith("/") && !value.startsWith("//") && !value.includes("\\");
}

/**
 * The `?redirect=` the challenge page may return to once satisfied: a
 * same-origin path that is not itself part of the sign-in round trip (a
 * stale or crafted `redirect=/mfa` would otherwise bounce forever). The
 * caller still applies the general open-redirect rule on top.
 */
export function mfaReturnTarget(redirectParam: string | null | undefined): string | null {
  if (!redirectParam || !isSameOriginPath(redirectParam)) return null;
  const queryStart = redirectParam.indexOf("?");
  const pathname = queryStart === -1 ? redirectParam : redirectParam.slice(0, queryStart);
  return isMfaExemptPath(pathname) ? null : redirectParam;
}

export interface MfaAttestationPayload {
  /** Firebase uid the attestation vouches for. */
  uid: string;
  /** The session token's auth_time (seconds) at attestation. */
  sat: number;
  /** When the attestation was minted (seconds). */
  iat: number;
}

const encoder = new TextEncoder();

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(value: string): Uint8Array | null {
  if (!/^[A-Za-z0-9_-]*$/.test(value)) return null;
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (value.length % 4)) % 4);
  try {
    const binary = atob(base64);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
  } catch {
    return null;
  }
}

async function hmacKey(secret: string, usage: KeyUsage): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", encoder.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, [usage]);
}

/** `base64url(json) + "." + base64url(HMAC-SHA256(base64url(json)))`. */
export async function signMfaAttestation(payload: MfaAttestationPayload, secret: string): Promise<string> {
  if (!secret) throw new Error("MFA attestation secret is not configured");
  const encodedPayload = toBase64Url(encoder.encode(JSON.stringify(payload)));
  const signature = await crypto.subtle.sign("HMAC", await hmacKey(secret, "sign"), encoder.encode(encodedPayload));
  return `${encodedPayload}.${toBase64Url(new Uint8Array(signature))}`;
}

/**
 * The attestation holds only when the signature verifies AND it names this
 * uid AND it was minted for a session with exactly this auth_time — a fresh
 * login (new session, new auth_time) silently invalidates the old cookie.
 */
export async function verifyMfaAttestation(
  value: string | null | undefined,
  secret: string,
  expected: { uid: string; sessionAuthTime: number },
): Promise<boolean> {
  if (!value || !secret) return false;
  const [encodedPayload, encodedSignature, ...rest] = value.split(".");
  if (!encodedPayload || !encodedSignature || rest.length > 0) return false;

  const signature = fromBase64Url(encodedSignature);
  const payloadBytes = fromBase64Url(encodedPayload);
  if (!signature || !payloadBytes) return false;

  const valid = await crypto.subtle.verify(
    "HMAC",
    await hmacKey(secret, "verify"),
    signature as BufferSource,
    encoder.encode(encodedPayload),
  );
  if (!valid) return false;

  let payload: Partial<MfaAttestationPayload>;
  try {
    payload = JSON.parse(new TextDecoder().decode(payloadBytes)) as Partial<MfaAttestationPayload>;
  } catch {
    return false;
  }
  return (
    typeof payload.uid === "string" &&
    payload.uid === expected.uid &&
    typeof payload.sat === "number" &&
    payload.sat === expected.sessionAuthTime
  );
}

/** Hosts where the browser accepts a cookie without the Secure attribute. */
export function isLocalhost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]" || hostname === "::1";
}

export function mfaCookieOptions(hostname: string) {
  return {
    name: MFA_COOKIE_NAME,
    httpOnly: true,
    secure: !isLocalhost(hostname),
    sameSite: "lax" as const,
    path: "/",
    maxAge: MFA_COOKIE_MAX_AGE_SECONDS,
  };
}

/**
 * The `Set-Cookie` line that expires the attestation cookie, for responses
 * whose cookie headers were written by someone else (the auth library's
 * logout response). It MUST be appended as a raw header: `response.cookies`
 * on a NextResponse snapshots the Set-Cookie headers at construction, and
 * any later `.set()` / `.delete()` re-serializes from that snapshot — which
 * silently dropped the library's `__session` clear and left every sign-out
 * with a live server session (2026-10-15 incident: "sign out just stays on
 * the platform").
 */
export function expireMfaCookieHeader(hostname: string): string {
  const secure = isLocalhost(hostname) ? "" : "; Secure";
  return `${MFA_COOKIE_NAME}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; HttpOnly${secure}; SameSite=Lax`;
}
